import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import StaffScanGate from '@/components/StaffScanGate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GsPlanPanel } from '@/components/processingJo/GsPlanPanel';
import { GsSlipPanel } from '@/components/processingJo/GsSlipPanel';
import { JoBadge, OverdueBadge, fmtDate, fmtN } from '@/components/processingJo/JoBits';
import { exportJoStockCsv } from '@/components/processingJo/exportJoStock';
import { joApi, joErr, sendToText } from '@/services/processingJoApi';

const ORDER_KEYS = ['jo_id', 'fg_name', 'design_name', 'shade', 'jobber_name', 'category', 'created_by_name'];
const SLIP_KEYS = ['slip_id', 'jo_id', 'fg_name', 'design_name'];
const matches = (row, keys, q) => !q || keys.some((k) => String(row[k] || '').toLowerCase().includes(q));
const istDay = (iso) => (iso ? new Date(new Date(iso).getTime() + 330 * 60000).toISOString().slice(0, 10) : '');
const inRange = (iso, from, to) => { const d = istDay(iso); return (!from || d >= from) && (!to || d <= to); };

const DateRange = ({ from, to, setFrom, setTo }) => (
  <div className="flex items-center gap-1.5 min-w-0 text-xs text-gray-600" data-testid="gs-jo-date-range">
    <span className="shrink-0">Raised</span>
    <Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="h-8 w-0 flex-1 min-w-0 bg-white text-xs px-1.5" data-testid="gs-jo-date-from" />
    <span className="shrink-0">to</span>
    <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="h-8 w-0 flex-1 min-w-0 bg-white text-xs px-1.5" data-testid="gs-jo-date-to" />
    {(from || to) && <button type="button" onClick={() => { setFrom(''); setTo(''); }} className="text-gray-400 hover:text-gray-700 shrink-0" aria-label="Clear dates" data-testid="gs-jo-date-clear"><X className="w-4 h-4" /></button>}
  </div>
);

const SearchBar = ({ q, setQ, count, total, filtered, exporting, onExport, children }) => (
  <div className="space-y-1">
    <div className="flex gap-2">
      <div className="relative flex-1 min-w-0">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search JO no, FG, design, shade, jobber…" className="pl-8 pr-8 h-9 bg-white" data-testid="gs-jo-search" />
        {q && <button type="button" onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" aria-label="Clear search" data-testid="gs-jo-search-clear"><X className="w-4 h-4" /></button>}
      </div>
      <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={onExport} disabled={exporting || !count} data-testid="gs-jo-export-btn">
        {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Export CSV</Button>
    </div>
    {children}
    {filtered && <p className="text-[11px] text-gray-500" data-testid="gs-jo-search-count">{count} of {total} shown</p>}
  </div>
);

const OrderCard = ({ o, active, onOpen }) => (
  <Card onClick={() => onOpen(o.jo_id)} data-testid={`gs-jo-card-${o.jo_id}`}
    className={`cursor-pointer transition-colors hover:border-[#2D5F4F] ${active ? 'border-[#2D5F4F] ring-1 ring-[#2D5F4F]' : ''}`}>
    <CardContent className="p-3 space-y-1">
      <div className="flex justify-between items-start gap-2"><span className="font-mono text-sm whitespace-nowrap shrink-0">{o.jo_id}</span><span className="flex flex-wrap justify-end items-start gap-1 [&>*]:whitespace-nowrap"><OverdueBadge days={o.days_late} testId={`gs-jo-card-overdue-${o.jo_id}`} /><JoBadge status={o.stage} /></span></div>
      <p className="text-sm truncate"><span className="font-mono">{o.fg_name}</span> <span className="text-gray-500">{o.design_name}</span></p>
      <p className="text-xs text-gray-600"><b className="text-amber-700" data-testid={`gs-jo-card-balance-${o.jo_id}`}>{fmtN(o.balance_to_send)}</b> of {fmtN(o.ordered_pcs)} pcs to send{o.live_slip_count ? ` · ${o.live_slip_count} slip(s) picking` : ''}</p>
      <p className="text-[11px] text-gray-400 truncate">{sendToText(o)} · {fmtDate(o.created_at)}</p>
    </CardContent>
  </Card>
);

const SlipCard = ({ s, active, onOpen }) => (
  <Card onClick={() => onOpen(s.slip_id)} data-testid={`gs-slip-card-${s.slip_id}`}
    className={`cursor-pointer transition-colors hover:border-[#2D5F4F] ${active ? 'border-[#2D5F4F] ring-1 ring-[#2D5F4F]' : ''}`}>
    <CardContent className="p-3 space-y-1">
      <div className="flex justify-between gap-2"><span className="font-mono text-sm">{s.slip_id}</span><JoBadge status={s.status} /></div>
      <p className="text-xs text-gray-600 font-mono truncate">{s.jo_id} · {s.fg_name}</p>
      <p className="text-xs text-gray-600">{fmtN(s.picked_pcs)} / {fmtN(s.pcs)} pcs picked · {s.bundles.length} bundles</p>
    </CardContent>
  </Card>
);

export default function GSProcessingJobOrders() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('to_pick');
  const [orders, setOrders] = useState([]);
  const [slips, setSlips] = useState([]);
  const [sel, setSel] = useState(null);
  const [slipId, setSlipId] = useState(null);
  const [canPlan, setCanPlan] = useState(false);
  useEffect(() => { joApi.meta().then((m) => setCanPlan(!!m.can_plan)).catch(() => {}); }, []);

  const load = useCallback(() => (tab === 'slips'
    ? joApi.slips().then(setSlips)
    : joApi.list({ status: 'OPEN', queue: tab === 'to_pick' ? 'to_pick' : undefined }).then(setOrders)).catch((e) => toast.error(joErr(e, 'Could not load'))), [tab]);
  useEffect(() => { load(); }, [load]);
  const openOrder = (id) => { setSlipId(null); joApi.get(id).then(setSel).catch((e) => toast.error(joErr(e, 'Could not open the job order'))); };
  const changed = () => { load(); if (sel) joApi.get(sel.jo_id).then(setSel).catch(() => {}); };
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const needle = q.trim().toLowerCase();
  const shownSlips = useMemo(() => slips.filter((s) => matches(s, SLIP_KEYS, needle) && inRange(s.jo_created_at, from, to)), [slips, needle, from, to]);
  const shownOrders = useMemo(() => orders.filter((o) => matches(o, ORDER_KEYS, needle) && inRange(o.created_at, from, to)), [orders, needle, from, to]);
  const filtered = !!(needle || from || to);
  const all = tab === 'slips' ? slips : orders;
  const list = tab === 'slips' ? shownSlips : shownOrders;
  const exportCsv = async () => {
    setExporting(true);
    try {
      const ids = [...new Set(list.map((r) => r.jo_id))];
      const r = await joApi.stockExport(ids);
      exportJoStockCsv(r.rows);
      toast.success(`Exported ${r.orders} job order(s) · ${r.rows.length} row(s)`);
    } catch (e) { toast.error(joErr(e, 'Could not export')); } finally { setExporting(false); }
  };

  return (
    <StaffScanGate stationName="Greige Storage — Processing Job Orders">
      <div className="min-h-screen bg-[#FFFBF5]" data-testid="gs-processing-jo">
        <header className="bg-[#1A1F2E] text-white shadow-lg pt-10 sm:pt-0">
          <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-16 py-2 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/sections/greige-storage')} className="text-white hover:bg-white/10" data-testid="gs-jo-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <div className="min-w-0"><h1 className="text-lg sm:text-xl font-bold truncate">Processing Job Orders</h1>
              <p className="text-xs text-[#E8DCC8] hidden sm:block">Pick base articles from stock in hand → pick slip → Internal Transfer to Processing</p></div>
            <Button variant="outline" className="ml-auto bg-transparent text-white border-white/30 hover:bg-white/10" onClick={load} data-testid="gs-jo-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5">
          <div className="space-y-3 min-w-0">
            <Tabs value={tab} onValueChange={(v) => { setTab(v); setSel(null); setSlipId(null); }}>
              <TabsList className="w-full">
                <TabsTrigger value="to_pick" className="flex-1" data-testid="gs-jo-tab-to-pick">To send</TabsTrigger>
                <TabsTrigger value="slips" className="flex-1" data-testid="gs-jo-tab-slips">Pick slips</TabsTrigger>
                <TabsTrigger value="all" className="flex-1" data-testid="gs-jo-tab-all">All open</TabsTrigger>
              </TabsList>
            </Tabs>
            <SearchBar q={q} setQ={setQ} count={list.length} total={all.length} filtered={filtered} exporting={exporting} onExport={exportCsv}>
              <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
            </SearchBar>
            {!list.length && <p className="text-sm text-gray-400 text-center py-8" data-testid="gs-jo-empty">{filtered && all.length ? 'No job order matches your search / dates' : 'Nothing here'}</p>}
            {tab === 'slips' ? shownSlips.map((s) => <SlipCard key={s.slip_id} s={s} active={slipId === s.slip_id} onOpen={(id) => { setSel(null); setSlipId(id); }} />)
              : shownOrders.map((o) => <OrderCard key={o.jo_id} o={o} active={sel?.jo_id === o.jo_id} onOpen={openOrder} />)}
          </div>
          <div className="min-w-0">
            {slipId ? <GsSlipPanel slipId={slipId} onBack={sel ? () => setSlipId(null) : undefined} onChanged={changed} />
              : sel ? <GsPlanPanel order={sel} canPlan={canPlan} onSlip={(s) => setSlipId(s.slip_id)} onChanged={changed} />
                : <p className="text-sm text-gray-400 pt-10 text-center">Pick a job order or a pick slip from the list</p>}
          </div>
        </main>
      </div>
    </StaffScanGate>
  );
}
